import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { BridgeService, DeviceSettingsRepository } from 'webcommon/legacy-common';
import { BusinessObjectUpdatedNotificationDto, GuidStrValue } from 'webcommon/shared';

@Injectable({
  providedIn: 'root',
})
export class BusinessObjectNotificationService {
  private subjectByType = new Map<string, Subject<BusinessObjectUpdatedNotificationDto>>();

  constructor(
    private readonly bridgeService: BridgeService,
    private deviceSettingsRepository: DeviceSettingsRepository,
  ) {}

  bootstrap(): void {
    this.bridgeService.on<BusinessObjectUpdatedNotificationDto>('notify-business-object-updated').subscribe(x => {
      this.onNotificationReceived(x);
    });
  }

  private onNotificationReceived(dto: BusinessObjectUpdatedNotificationDto) {
    const subject = this.subjectByType.get(dto.BusinessObjectName);
    if (!subject) {
      return;
    }

    subject.next(dto);
  }

  notifyBusinessObjectUpdate(businessObjectName: string, uid: GuidStrValue) {
    if (!this.deviceSettingsRepository.isStandaloneBrowser) {
      this.bridgeService.fireEvent('notify-business-object-update', {
        BusinessObjectName: businessObjectName,
        Uid: uid,
      });
    } else {
      this.onNotificationReceived({
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Parms: null,
      });
    }
  }

  notifyBusinessObjectUpdateWithString(businessObjectName: string, uid: GuidStrValue, param: string) {
    if (!this.deviceSettingsRepository.isStandaloneBrowser) {
      this.bridgeService.fireEvent('notify-business-object-update-with-string', {
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Param: param,
      });
    } else {
      this.onNotificationReceived({
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Parms: [ param ],
      });
    }
  }

  notifyBusinessObjectUpdateWithGuid(businessObjectName: string, uid: GuidStrValue, param: GuidStrValue) {
    if (!this.deviceSettingsRepository.isStandaloneBrowser) {
      this.bridgeService.fireEvent('notify-business-object-update-with-guid', {
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Param: param,
      });
    } else {
      this.onNotificationReceived({
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Parms: [ param ],
      });
    }
  }

  notifyBusinessObjectUpdateWithGuidString(businessObjectName: string, uid: GuidStrValue, param1: GuidStrValue, param2: string) {
    if (!this.deviceSettingsRepository.isStandaloneBrowser) {
      this.bridgeService.fireEvent('notify-business-object-update-with-guid-string', {
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Param1: param1,
        Param2: param2,
      });
    } else {
      this.onNotificationReceived({
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Parms: [ param1, param2 ],
      });
    }
  }

  notifyBusinessObjectUpdateWithBoolean(businessObjectName: string, uid: GuidStrValue, param: boolean) {
    if (!this.deviceSettingsRepository.isStandaloneBrowser) {
      this.bridgeService.fireEvent('notify-business-object-update-with-boolean', {
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Param: param,
      });
    } else {
      this.onNotificationReceived({
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Parms: [ param ],
      });
    }
  }

  notifyBusinessObjectUpdateWithDateTime(businessObjectName: string, uid: GuidStrValue, param: Date) {
    if (!this.deviceSettingsRepository.isStandaloneBrowser) {
      this.bridgeService.fireEvent('notify-business-object-update-with-datetime', {
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Param: param,
      });
    } else {
      this.onNotificationReceived({
        BusinessObjectName: businessObjectName,
        Uid: uid,
        Parms: [ param ],
      });
    }
  }

  subscribeBusinessObjectUpdated(businessObjectName: string, uids?: GuidStrValue[] | null) {
    if (!businessObjectName) {
      throw new Error('subscribeBusinessObjectUpdated called with invalid businessObjectName');
    }
    if (uids && uids.length === 0) {
      throw new Error('subscribeBusinessObjectUpdated called with empty array of uids');
    }

    let subject = this.subjectByType.get(businessObjectName);
    if (!subject) {
      subject = new Subject();
      this.subjectByType.set(businessObjectName, subject);
    }

    return subject.asObservable().pipe(
      filter(x => {
        if (uids && uids.indexOf(x.Uid) === -1) {
          return false;
        }

        return true;
      }),
    );
  }
}
